import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';
import { Link } from '@reach/router';
import { useSetStepStatus } from '../../../hooks';
import { useSelector } from 'react-redux';
import { 
         getSelectedPlan,
         isBasicInfoFormAnswered,
         isBasicClientFormAnswered,
         isGeneralInformationFormAnswered,
         isRecommendationFormAnswered,

} from '../../../store/policystreamStore';
import type { StoreState } from '../../../store';
import { ProgressStep } from './PolicyStreamProgressBar';

interface MissingInformationProps {
  missing: string;
  redirectURL: string;
  stepName?: ProgressStep['name'];
}

export const MissingInformation = ({
  missing, redirectURL, stepName
}: MissingInformationProps) => {
  // If stepName is not provided, default to 'Basic Information'. But note that in the case
  // that stepName is not provided, the setStepStatus will not be called.
  const setStepStatus = useSetStepStatus(stepName ?? 'Basic Information');
  
  return (
    <MissingInformationContainer>
      <Typography variant='h3' sx={{ mb: 2 }}>
        Critical Information is Missing!
      </Typography>
      <Typography>
        Unfortunately, we are missing your {missing}, which is critical for us
        to get an accurate quote. Please fill out the{' '}
        <Link 
          to={redirectURL}
          onClick={() => {
            if (stepName) {
              setStepStatus('NOT_VISITED');
            }
          }}
        >
          required information
        </Link>{' '}
        and try again!
      </Typography>
    </MissingInformationContainer>
  );
};

function createMissingGuard(
  formCheck: (state: StoreState) => boolean,
  missingText: string
) {
  return () => {
    const submitted = useSelector(formCheck);
    if (!submitted) {
      return { missing: missingText };
    }
    return null;
  };
}

export const useMissingGeneralInformationGuard = createMissingGuard(
  isGeneralInformationFormAnswered,
  'General Information Form'
);

export const useMissingClientInfoGuard = createMissingGuard(
  isBasicClientFormAnswered,
  'Basic Client Information Form'
);

export const useMissingRecommendationFormGuard = createMissingGuard(
  isRecommendationFormAnswered,
  'Needs Analysis Form'
);

export const useMissingBasicInfoGuard = createMissingGuard(
  isBasicInfoFormAnswered,
  'Basic Information Form'
);

// This function doesn't use the hook factory pattern because getSelectedPlan doesn't return a boolean,
// and the code is easier to understand if this is its own function instead of having a very awkward
// type definition for the formCheck parameter of createMissingGuard. 
export function useMissingPlanSelectionGuard() {
  const selectedPlan = useSelector(getSelectedPlan);

  if (!selectedPlan) {
    return { missing: 'Insurance Plan' };
  }

  return null;
}

const MissingInformationContainer = styled('div')(({ theme }) => {
  return {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  };
});